<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->


<template>
  <div id="app" class="wrapper">
    <notifications position="top center" width="30%" :max="5">
      <template #body="props">
        <div class="notification vue-notification" :class="props.item.type">
          <button class="delete" @click="props.close"></button>
          <strong class="notification-title">
            {{ props.item.title }}
          </strong>
          <div class="notification-content" v-html="sanitizedNotificationText(props.item.text)"></div>
        </div>
      </template>
    </notifications>

    <template v-if="!loading">
      <div class="box error" v-if="communicationError">
        <h2>
          {{ $t('communication-error') }}
        </h2>
        {{ $t('core-cannot-be-reached') }}
      </div>

      <template v-else-if="currentUser || $keycloak.hasTemporaryToken">
        <cytomine-navbar />
        <div class="bottom">
          <keep-alive include="cytomine-storage">
            <router-view />
          </keep-alive>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import { initializeApiClient } from '@/bootstrap/api-client.js';
import { bootstrapRuntimeConfig } from '@/bootstrap/config.js';
import {
  isCommunicationError,
  isSessionActive,
  refreshSessionState,
  registerWakeupHandler,
  unregisterWakeupHandler,
} from '@/bootstrap/session.js';
import constants from '@/utils/constants.js';
import { sanitizeHtml } from '@/utils/sanitize-html';
import { get } from '@/utils/store-helpers';
import { updateToken } from '@/utils/token-utils';
import { changeLanguageMixin } from '@/lang.js';

import CytomineNavbar from '@/components/navbar/CytomineNavbar.vue';

export default {
  name: 'app',
  components: {
    CytomineNavbar,
  },
  mixins: [
    changeLanguageMixin,
  ],
  data() {
    return {
      communicationError: false,
      loading: true,
      timeout: null,
    };
  },
  computed: {
    currentUser: get('currentUser/user'),
    currentAccount: get('currentUser/account'),
    project: get('currentProject/project')
  },
  watch: {
    $route() {
      // Invoke refresh token if needed when route changes.
      updateToken();
    },
  },
  methods: {
    wakeup: async function () {
      if (!isSessionActive()) {
        return;
      }
      await updateToken();
      await this.refreshSession();
    },
    async refreshSession() {
      if (!isSessionActive()) {
        return; // window not visible or inactive user => stop pinging
      }
      try {
        await refreshSessionState({
          store: this.$store,
          keycloak: this.$keycloak,
          currentUser: this.currentUser,
          currentAccount: this.currentAccount,
          changeLanguage: this.changeLanguage,
        });
        this.communicationError = false;
      } catch (error) {
        console.error(error);
        this.communicationError = isCommunicationError(error);
      }

      clearTimeout(this.timeout);
      this.timeout = setTimeout(this.refreshSession, constants.PING_INTERVAL);
    },

    sanitizedNotificationText(text) {
      return sanitizeHtml(text);
    },
  },
  async created() {
    await bootstrapRuntimeConfig();
    initializeApiClient({
      store: this.$store,
      keycloak: this.$keycloak,
    });

    await this.refreshSession();
    this.loading = false;
    registerWakeupHandler(this.wakeup);
  },
  beforeDestroy() {
    clearTimeout(this.timeout);
    unregisterWakeupHandler(this.wakeup);
  }
};
</script>

<style lang="scss">
@import './assets/styles/main.scss';
</style>
